import axiosInstance from '../../../shared/api/axiosInstance';
import type { InventoryTenantSettings } from '../../../generated/api/models/inventoryTenantSettings';
import type { InventoryTenantSettingsUpdate } from '../../../generated/api/models/inventoryTenantSettingsUpdate';
import type { MovementListResponse } from '../../../generated/api/models/movementListResponse';
import type {
  Warehouse,
  WarehouseCreate,
  WarehouseUpdate,
  Location,
  LocationCreate,
  LocationUpdate,
  LocationGenerateRequest,
  LocationGenerateResult,
  LocationBulkStatusRequest,
  LocationBulkDeleteRequest,
  LocationBulkResult,
  StockLevel,
  StockLevelSummary,
  StockRollup,
  StockLot,
  StockLotCreate,
  ListStockLotsParams,
  StockMovement,
  StockAdjustment,
  StockAdjustmentCreate,
  StockTransfer,
  StockTransferCreate,
  StockTransferItem,
  StockTransferItemCreate,
  StockTake,
  StockTakeCreate,
  StockTakeItem,
  StockTakeItemCreate,
  StockTakeItemCountUpdate,
  BarcodeProductLookup,
  WarehouseVarianceSeries,
  TopVarianceProduct,
  ProductCountHistoryEntry,
  ReorderRule,
  ReorderRuleCreate,
  ReorderRuleUpdate,
  ReorderAlert,
  InventoryDashboard,
  ReplayGLPostingsResult,
  StockMovementReport,
  StockMovementReportParams,
  ReorderAdviceReport,
  ReorderAdviceReportParams,
  ExpiredLotsReport,
  ExpiredLotsReportParams,
  StockValuationReport,
  ValuationReportParams,
  ListWarehousesParams,
  ListStockLevelsParams,
  StockLevelLookupParams,
  ListStockMovementsParams,
  ListStockAdjustmentsParams,
  ListStockTransfersParams,
  ListStockTakesParams,
  ProductInquiry,
  InquiryReserveRequest,
  InquiryReserveResult,
  InquiryPoHistoryRow,
  InquiryVendorPricingRow,
  InquiryReservationRow,
  ProductWarehouseUpdate,
  ReorderRecalcRequest,
  ReorderRecalcResult,
  InventoryUserSettings,
  InventoryUserSettingsData,
  BarcodeLookupResult,
  PackagingChangeRequest,
  DemandPredictionSuggestion,
  PrePositionResult,
  NotifyAeResult,
  AsyncPostingJobRef,
  PostingJobStatus,
} from '../types';

const BASE = '/api/v1/inventory';

// Warehouse API
export const warehouseApi = {
  list: async (params?: ListWarehousesParams): Promise<Warehouse[]> => {
    const response = await axiosInstance.get(`${BASE}/warehouses`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<Warehouse> => {
    const response = await axiosInstance.get(`${BASE}/warehouses/${id}`);
    return response.data.data;
  },

  create: async (data: WarehouseCreate): Promise<Warehouse> => {
    const response = await axiosInstance.post(`${BASE}/warehouses`, data);
    return response.data.data;
  },

  update: async (id: string, data: WarehouseUpdate): Promise<Warehouse> => {
    const response = await axiosInstance.patch(`${BASE}/warehouses/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE}/warehouses/${id}`);
  },
};

// The location tree fetches every node in one request and builds the hierarchy
// client-side. Matches the backend generator/list ceiling (MAX_GENERATED_LOCATIONS).
const LOCATION_TREE_LIMIT = 10000;

// Location API
export const locationApi = {
  // Fetch all locations of a warehouse for the tree view. The backend caps the
  // page at the generator's node ceiling, so one request covers a whole
  // generated warehouse.
  list: async (warehouseId: string): Promise<Location[]> => {
    const response = await axiosInstance.get(`${BASE}/warehouses/${warehouseId}/locations`, {
      params: { limit: LOCATION_TREE_LIMIT },
    });
    return response.data.data;
  },

  get: async (id: string): Promise<Location> => {
    const response = await axiosInstance.get(`${BASE}/locations/${id}`);
    return response.data.data;
  },

  // Stock held in a location and everything nested under it (subtree rollup).
  getStock: async (id: string): Promise<StockLevel[]> => {
    const response = await axiosInstance.get(`${BASE}/locations/${id}/stock`);
    return response.data.data;
  },

  create: async (warehouseId: string, data: LocationCreate): Promise<Location> => {
    const response = await axiosInstance.post(`${BASE}/warehouses/${warehouseId}/locations`, data);
    return response.data.data;
  },

  update: async (id: string, data: LocationUpdate): Promise<Location> => {
    const response = await axiosInstance.patch(`${BASE}/locations/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE}/locations/${id}`);
  },

  // Bulk activate/deactivate the given locations.
  bulkSetStatus: async (
    warehouseId: string,
    data: LocationBulkStatusRequest
  ): Promise<LocationBulkResult> => {
    const response = await axiosInstance.post(
      `${BASE}/warehouses/${warehouseId}/locations/bulk-status`,
      data
    );
    return response.data.data;
  },

  // Bulk soft-delete the given locations and their descendants (subtree delete).
  bulkDelete: async (
    warehouseId: string,
    data: LocationBulkDeleteRequest
  ): Promise<LocationBulkResult> => {
    const response = await axiosInstance.post(
      `${BASE}/warehouses/${warehouseId}/locations/bulk-delete`,
      data
    );
    return response.data.data;
  },

  // Bulk generate a hierarchy of locations from dimension specs. The same
  // endpoint powers the full multi-dimension wizard and the per-node quick-fill;
  // `dry_run: true` returns the preview (counts, sample codes, collisions)
  // without persisting. See warehouse-locations-design § Generation model.
  generate: async (
    warehouseId: string,
    data: LocationGenerateRequest
  ): Promise<LocationGenerateResult> => {
    const response = await axiosInstance.post(
      `${BASE}/warehouses/${warehouseId}/locations/generate`,
      data
    );
    return response.data.data;
  },
};

// Stock Levels API
export const stockLevelApi = {
  list: async (params?: ListStockLevelsParams): Promise<StockLevel[]> => {
    const response = await axiosInstance.get(`${BASE}/stock-levels`, {
      params,
      paramsSerializer: { indexes: null },
    });
    return response.data.data;
  },

  summary: async (productId: string): Promise<StockLevelSummary> => {
    const response = await axiosInstance.get(`${BASE}/stock-levels/product/${productId}`);
    return response.data.data;
  },

  rollup: async (productId: string, warehouseId?: string): Promise<StockRollup> => {
    const response = await axiosInstance.get(
      `${BASE}/stock-levels/product/${productId}/rollup`,
      { params: warehouseId ? { warehouse_id: warehouseId } : undefined },
    );
    return response.data.data;
  },

  lookup: async (params: StockLevelLookupParams): Promise<StockLevel | null> => {
    const response = await axiosInstance.get(`${BASE}/stock-levels/lookup`, { params });
    return response.data.data;
  },
};

// Stock Lots API
export const stockLotApi = {
  list: async (params: ListStockLotsParams): Promise<StockLot[]> => {
    const response = await axiosInstance.get(`${BASE}/lots`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<StockLot> => {
    const response = await axiosInstance.get(`${BASE}/lots/${id}`);
    return response.data.data;
  },

  create: async (data: StockLotCreate): Promise<StockLot> => {
    const response = await axiosInstance.post(`${BASE}/lots`, data);
    return response.data.data;
  },
};

// Stock Movements API
export const stockMovementApi = {
  list: async (params?: ListStockMovementsParams): Promise<MovementListResponse> => {
    // indexes:null serializes movement_types as repeated `movement_types=a&movement_types=b`
    // (no `[]`), which FastAPI's `list[str] = Query()` parses — same as stock-levels.
    const response = await axiosInstance.get<MovementListResponse>(`${BASE}/movements`, {
      params,
      paramsSerializer: { indexes: null },
    });
    return response.data;
  },

  get: async (id: string): Promise<StockMovement> => {
    const response = await axiosInstance.get(`${BASE}/movements/${id}`);
    return response.data.data;
  },
};

// Packaging change (open / pack) API — atomic 2-row ledger events that
// decompose packaged stock into base units or consolidate base back into packs.
export const packagingChangeApi = {
  open: async (data: PackagingChangeRequest): Promise<StockMovement[]> => {
    const response = await axiosInstance.post(`${BASE}/packaging/open`, data);
    return response.data.data;
  },

  pack: async (data: PackagingChangeRequest): Promise<StockMovement[]> => {
    const response = await axiosInstance.post(`${BASE}/packaging/pack`, data);
    return response.data.data;
  },
};

// Stock Adjustments API
export const stockAdjustmentApi = {
  list: async (params?: ListStockAdjustmentsParams): Promise<StockAdjustment[]> => {
    const response = await axiosInstance.get(`${BASE}/adjustments`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<StockAdjustment> => {
    const response = await axiosInstance.get(`${BASE}/adjustments/${id}`);
    return response.data.data;
  },

  create: async (data: StockAdjustmentCreate): Promise<StockAdjustment> => {
    const response = await axiosInstance.post(`${BASE}/adjustments`, data);
    return response.data.data;
  },

  // Large posts (>= the sysadmin threshold) return an AsyncPostingJobRef (202)
  // instead of the posted adjustment; poll inventoryJobApi.getStatus to follow it.
  post: async (id: string): Promise<StockAdjustment | AsyncPostingJobRef> => {
    const response = await axiosInstance.post(`${BASE}/adjustments/${id}/post`);
    return response.data.data;
  },

  void: async (id: string): Promise<StockAdjustment> => {
    const response = await axiosInstance.post(`${BASE}/adjustments/${id}/void`);
    return response.data.data;
  },
};

// Stock Transfers API
export const stockTransferApi = {
  list: async (params?: ListStockTransfersParams): Promise<StockTransfer[]> => {
    const response = await axiosInstance.get(`${BASE}/transfers`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<StockTransfer> => {
    const response = await axiosInstance.get(`${BASE}/transfers/${id}`);
    return response.data.data;
  },

  create: async (data: StockTransferCreate): Promise<StockTransfer> => {
    const response = await axiosInstance.post(`${BASE}/transfers`, data);
    return response.data.data;
  },

  // Large transfers (>= the sysadmin threshold) return an AsyncPostingJobRef (202)
  // instead of the transfer; poll inventoryJobApi.getStatus to follow it.
  ship: async (id: string): Promise<StockTransfer | AsyncPostingJobRef> => {
    const response = await axiosInstance.post(`${BASE}/transfers/${id}/ship`);
    return response.data.data;
  },

  receive: async (id: string): Promise<StockTransfer | AsyncPostingJobRef> => {
    const response = await axiosInstance.post(`${BASE}/transfers/${id}/receive`);
    return response.data.data;
  },

  cancel: async (id: string): Promise<StockTransfer> => {
    const response = await axiosInstance.post(`${BASE}/transfers/${id}/cancel`);
    return response.data.data;
  },

  addItem: async (transferId: string, data: StockTransferItemCreate): Promise<StockTransferItem> => {
    const response = await axiosInstance.post(`${BASE}/transfers/${transferId}/items`, data);
    return response.data.data;
  },

  removeItem: async (transferId: string, itemId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE}/transfers/${transferId}/items/${itemId}`);
  },
};

// Stock Takes API
export const stockTakeApi = {
  list: async (params?: ListStockTakesParams): Promise<StockTake[]> => {
    const response = await axiosInstance.get(`${BASE}/stock-takes`, { params });
    return response.data.data;
  },

  get: async (id: string): Promise<StockTake> => {
    const response = await axiosInstance.get(`${BASE}/stock-takes/${id}`);
    return response.data.data;
  },

  create: async (data: StockTakeCreate): Promise<StockTake> => {
    const response = await axiosInstance.post(`${BASE}/stock-takes`, data);
    return response.data.data;
  },

  // Large stock takes (>= the sysadmin threshold) return an AsyncPostingJobRef
  // (202) instead of the stock take; poll inventoryJobApi.getStatus to follow it.
  complete: async (id: string): Promise<StockTake | AsyncPostingJobRef> => {
    const response = await axiosInstance.post(`${BASE}/stock-takes/${id}/complete`);
    return response.data.data;
  },

  start: async (id: string): Promise<StockTake> => {
    const response = await axiosInstance.post(`${BASE}/stock-takes/${id}/start`);
    return response.data.data;
  },

  cancel: async (id: string): Promise<StockTake> => {
    const response = await axiosInstance.post(`${BASE}/stock-takes/${id}/cancel`);
    return response.data.data;
  },

  addItem: async (stockTakeId: string, data: StockTakeItemCreate): Promise<StockTakeItem> => {
    const response = await axiosInstance.post(`${BASE}/stock-takes/${stockTakeId}/items`, data);
    return response.data.data;
  },

  updateItemCount: async (
    stockTakeId: string,
    itemId: string,
    data: StockTakeItemCountUpdate,
  ): Promise<StockTakeItem> => {
    const response = await axiosInstance.patch(
      `${BASE}/stock-takes/${stockTakeId}/items/${itemId}`,
      data,
    );
    return response.data.data;
  },

  removeItem: async (stockTakeId: string, itemId: string): Promise<void> => {
    await axiosInstance.delete(`${BASE}/stock-takes/${stockTakeId}/items/${itemId}`);
  },

  /** Look up a product by scanned barcode/SKU against the live server. */
  findProductByBarcode: async (barcode: string): Promise<BarcodeProductLookup | null> => {
    const response = await axiosInstance.get(`${BASE}/ai/find-product-by-barcode`, {
      params: { barcode },
    });
    return response.data.data;
  },

  /** Barcode/SKU → product index for a warehouse (prefetched for offline scanning). */
  barcodeIndex: async (warehouseId: string): Promise<BarcodeProductLookup[]> => {
    const response = await axiosInstance.get(`${BASE}/stock-takes/barcode-index`, {
      params: { warehouse_id: warehouseId },
    });
    return response.data.data;
  },
};

// Stock Take History API
export const stockTakeHistoryApi = {
  warehouseVariance: async (days = 90): Promise<WarehouseVarianceSeries[]> => {
    const response = await axiosInstance.get(`${BASE}/stock-takes/history/warehouse-variance`, {
      params: { days },
    });
    return response.data.data;
  },

  topVarianceProducts: async (params: { limit?: number; days?: number } = {}): Promise<TopVarianceProduct[]> => {
    const response = await axiosInstance.get(`${BASE}/stock-takes/history/top-variance-products`, {
      params,
    });
    return response.data.data;
  },

  productHistory: async (productId: string, limit = 50): Promise<ProductCountHistoryEntry[]> => {
    const response = await axiosInstance.get(`${BASE}/stock-takes/history/product/${productId}`, {
      params: { limit },
    });
    return response.data.data;
  },
};

// Reorder Rules API
export const reorderRuleApi = {
  create: async (data: ReorderRuleCreate): Promise<ReorderRule> => {
    const response = await axiosInstance.post(`${BASE}/reorder-rules`, data);
    return response.data.data;
  },

  update: async (id: string, data: ReorderRuleUpdate): Promise<ReorderRule> => {
    const response = await axiosInstance.patch(`${BASE}/reorder-rules/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`${BASE}/reorder-rules/${id}`);
  },

  alerts: async (): Promise<ReorderAlert[]> => {
    const response = await axiosInstance.get(`${BASE}/reorder-alerts`);
    return response.data.data;
  },

  snooze: async (ruleId: string, days: number): Promise<ReorderRule> => {
    const response = await axiosInstance.post(`${BASE}/reorder-rules/${ruleId}/snooze`, { days });
    return response.data.data;
  },

  listByProduct: async (productId: string): Promise<ReorderRule[]> => {
    const response = await axiosInstance.get(`${BASE}/reorder-rules`, {
      params: { product_id: productId, limit: 200 },
    });
    return response.data.data;
  },
};

// Settings API
export const settingsApi = {
  getTenantSettings: async (): Promise<InventoryTenantSettings> => {
    const response = await axiosInstance.get(`${BASE}/settings/tenant`);
    return response.data;
  },

  updateTenantSettings: async (data: InventoryTenantSettingsUpdate): Promise<InventoryTenantSettings> => {
    const response = await axiosInstance.put(`${BASE}/settings/tenant`, data);
    return response.data;
  },

  getUserSettings: async (): Promise<InventoryUserSettings> => {
    const response = await axiosInstance.get(`${BASE}/settings/user`);
    return response.data;
  },

  updateUserSettings: async (settings: InventoryUserSettingsData): Promise<InventoryUserSettings> => {
    const response = await axiosInstance.put(`${BASE}/settings/user`, { settings });
    return response.data;
  },
};

// Barcode lookup — resolves a scanned code (barcode, variant SKU, or product
// SKU) to a product + stock summary. Returns null when nothing matches.
export const barcodeApi = {
  lookup: async (code: string): Promise<BarcodeLookupResult | null> => {
    const response = await axiosInstance.get(`${BASE}/ai/find-product-by-barcode`, {
      params: { barcode: code },
    });
    return response.data.data ?? null;
  },
};

// Dashboard API
export const inventoryDashboardApi = {
  // On the shared KPI substrate: the response body IS the model — no {data: ...}
  // envelope to unwrap (docs/platform/dashboard-kpi-endpoints.md §1).
  get: async (): Promise<InventoryDashboard> => {
    const response = await axiosInstance.get(`${BASE}/dashboard/stats`);
    return response.data;
  },

  replayFailedGLPostings: async (): Promise<ReplayGLPostingsResult> => {
    const response = await axiosInstance.post(`${BASE}/gl-postings/replay`);
    return response.data.data;
  },
};

// AI customer-aware demand predictions (W9)
export const demandPredictionApi = {
  listSuggestions: async (limit = 5): Promise<DemandPredictionSuggestion[]> => {
    // On the KPI substrate: the body is the InventoryAiSuggestions model
    // ({ suggestions: [...] }), not the {data: ...} envelope.
    const response = await axiosInstance.get(`${BASE}/dashboard/ai-suggestions`, {
      params: { limit },
    });
    return response.data.suggestions;
  },

  prePosition: async (predictionId: string): Promise<PrePositionResult> => {
    const response = await axiosInstance.post(
      `${BASE}/demand-predictions/${predictionId}/pre-position`,
    );
    return response.data.data;
  },

  notifyAe: async (predictionId: string): Promise<NotifyAeResult> => {
    const response = await axiosInstance.post(
      `${BASE}/demand-predictions/${predictionId}/notify-ae`,
    );
    return response.data.data;
  },

  dismiss: async (predictionId: string): Promise<void> => {
    await axiosInstance.post(`${BASE}/demand-predictions/${predictionId}/dismiss`);
  },
};

// Stock Inquiry API (T8)
export const stockInquiryApi = {
  get: async (productId: string, variantId?: string): Promise<ProductInquiry> => {
    const response = await axiosInstance.get(`${BASE}/inquiry/product/${productId}`, {
      params: variantId ? { variant_id: variantId } : undefined,
    });
    return response.data.data;
  },

  reserve: async (data: InquiryReserveRequest): Promise<InquiryReserveResult> => {
    const response = await axiosInstance.post(`${BASE}/inquiry/reserve`, data);
    return response.data.data;
  },

  poHistory: async (productId: string, limit = 50): Promise<InquiryPoHistoryRow[]> => {
    const response = await axiosInstance.get(
      `${BASE}/inquiry/product/${productId}/po-history`,
      { params: { limit } },
    );
    return response.data.data;
  },

  vendorPricing: async (productId: string): Promise<InquiryVendorPricingRow[]> => {
    const response = await axiosInstance.get(
      `${BASE}/inquiry/product/${productId}/vendor-pricing`,
    );
    return response.data.data;
  },

  reservations: async (
    productId: string,
    variantId?: string,
    limit = 100,
  ): Promise<InquiryReservationRow[]> => {
    const response = await axiosInstance.get(
      `${BASE}/inquiry/product/${productId}/reservations`,
      { params: { limit, ...(variantId ? { variant_id: variantId } : {}) } },
    );
    return response.data.data;
  },

  updateProductWarehouse: async (data: ProductWarehouseUpdate): Promise<void> => {
    await axiosInstance.put(`${BASE}/inquiry/product-warehouse`, data);
  },

  recalcReorderPoints: async (
    data: ReorderRecalcRequest,
  ): Promise<ReorderRecalcResult> => {
    const response = await axiosInstance.post(`${BASE}/reorder-rules/recalc`, data);
    return response.data.data;
  },
};

// Operational Reports API (T13)
export const REPORT_ENDPOINTS = {
  movements: `${BASE}/reports/movements`,
  reorderAdvice: `${BASE}/reports/reorder-advice`,
  expiredLots: `${BASE}/reports/expired-lots`,
  valuation: `${BASE}/reports/valuation`,
} as const;

export const inventoryReportApi = {
  stockMovement: async (
    params: StockMovementReportParams
  ): Promise<StockMovementReport> => {
    const response = await axiosInstance.get(REPORT_ENDPOINTS.movements, { params });
    return response.data.data;
  },

  reorderAdvice: async (
    params: ReorderAdviceReportParams
  ): Promise<ReorderAdviceReport> => {
    const response = await axiosInstance.get(REPORT_ENDPOINTS.reorderAdvice, { params });
    return response.data.data;
  },

  expiredLots: async (
    params: ExpiredLotsReportParams
  ): Promise<ExpiredLotsReport> => {
    const response = await axiosInstance.get(REPORT_ENDPOINTS.expiredLots, { params });
    return response.data.data;
  },

  valuation: async (
    params: ValuationReportParams
  ): Promise<StockValuationReport> => {
    const response = await axiosInstance.get(REPORT_ENDPOINTS.valuation, { params });
    return response.data.data;
  },
};

// Async large-posting job status — polled after a poster returns a 202 + job ref.
export const inventoryJobApi = {
  getStatus: async (jobId: string): Promise<PostingJobStatus> => {
    const response = await axiosInstance.get(`${BASE}/jobs/${jobId}`);
    return response.data.data;
  },
};

// Stock movement wizard — open PO/SO lookup for the inventory Receive/Issue flows.
export interface WizardOpenPOItem {
  po_item_id: string;
  product_id: string | null;
  variant_id: string | null;
  description: string | null;
  quantity_ordered: number;
  quantity_received: number;
  quantity_outstanding: number;
  unit_cost: number;
  uom: string | null;
}
export interface WizardOpenPurchaseOrder {
  id: string;
  po_number: string;
  vendor_id: string;
  status: string;
  order_date: string | null;
  expected_delivery_date: string | null;
  warehouse_id: string | null;
  currency_code: string | null;
  items: WizardOpenPOItem[];
}

export interface WizardOpenSOItem {
  sales_order_item_id: string;
  product_id: string | null;
  variant_id: string | null;
  description: string | null;
  quantity_ordered: number;
  quantity_shipped: number;
  quantity_outstanding: number;
  unit_price: number;
  uom: string | null;
}
export interface WizardOpenSalesOrder {
  id: string;
  order_number: string;
  customer_id: string;
  status: string;
  order_date: string | null;
  requested_delivery_date: string | null;
  warehouse_id: string | null;
  currency_code: string | null;
  items: WizardOpenSOItem[];
}

export const stockMovementWizardApi = {
  listOpenPurchaseOrders: async (vendorId: string): Promise<WizardOpenPurchaseOrder[]> => {
    const response = await axiosInstance.get(`${BASE}/wizard/open-purchase-orders`, {
      params: { vendor_id: vendorId },
    });
    return response.data.data;
  },

  listOpenSalesOrders: async (customerId: string): Promise<WizardOpenSalesOrder[]> => {
    const response = await axiosInstance.get(`${BASE}/wizard/open-sales-orders`, {
      params: { customer_id: customerId },
    });
    return response.data.data;
  },
};
